import React, { FC, useLayoutEffect, useRef, useState } from 'react'
import { UISizes, UITypes, i18n, LinkComponent, TooltipComponent, Indent } from 'LibNeo'
import { Callback } from 'Src/types/utilTypes'
import { getValidLink, XOR } from 'Helpers'
import {
	ActionsWrapper,
	BannerText,
	ContentContainer,
	Icon,
	IconClose,
	NotificationContainer,
	NotificationDescription,
	NotificationTextWrapper,
	NotificationTitle,
} from './styled'

const iconName = (type): string => {
	switch (type) {
		case UITypes.PRIMARY:
			return 'check-checked'
		case UITypes.WARNING:
			return 'attention'
		case UITypes.DANGER:
			return 'error'
		case UITypes.PASSIVE:
		case UITypes.INFO:
			return 'info'
		default:
			return null
	}
}

export type BannerUITypes = UITypes.PRIMARY | UITypes.DANGER | UITypes.WARNING | UITypes.INFO
export type NotificationUITypes =
	| UITypes.PRIMARY
	| UITypes.DANGER
	| UITypes.WARNING
	| UITypes.INFO
	| UITypes.PASSIVE

type FormNotificationProps = {
	onClose?: Callback
	className?: string
	icon?: string
	isClosedBanner?: boolean
} & XOR<
	{
		isBanner: boolean
		type?: BannerUITypes
		linkTitle?: string
		message: string
		url?: string
		external?: boolean
	},
	{
		title?: string
		type?: NotificationUITypes
		hideIcon?: boolean
		onActionClick?: Callback
		message?: string | JSX.Element
		helperLinkTitle?: string // Обязательно передается вместе с функцией onHelperClick
		actionLinkTitle?: string // Обязательно передается вместе с функцией onActionClick
		onHelperClick?: Callback
		helperLinkTooltip?: string
		actionLinkTooltip?: string
	}
>

export const FormNotificationComponent: FC<FormNotificationProps> = ({
	type = UITypes.PRIMARY,
	title,
	actionLinkTitle,
	linkTitle,
	helperLinkTitle,
	message,
	isBanner,
	hideIcon = false,
	external = false,
	url,
	onClose,
	onActionClick,
	onHelperClick,
	helperLinkTooltip,
	actionLinkTooltip,
	className,
	icon,
	isClosedBanner,
}) => {
	const label = i18n(title)

	const notifContainerRef = useRef(null)
	const [heightNotifContainer, setHeightNotifContainer] = useState(0)

	const actionLink = (
		<LinkComponent
			type={type === UITypes.PASSIVE ? UITypes.PRIMARY : type}
			title={actionLinkTitle}
			onClick={onActionClick}
			size={UISizes.SMALL}
		/>
	)
	const helperLink = (
		<LinkComponent
			size={UISizes.SMALL}
			title={helperLinkTitle}
			onClick={onHelperClick}
			type={UITypes.PASSIVE}
		/>
	)

	useLayoutEffect(() => {
		setHeightNotifContainer(notifContainerRef?.current?.offsetHeight)
	}, [])

	return (
		<NotificationContainer
			type={type}
			isBanner={isBanner}
			isClosedBanner={isClosedBanner}
			heightNotifContainer={heightNotifContainer}
			className={className}
			ref={notifContainerRef}
		>
			<ContentContainer isBanner={isBanner}>
				{!hideIcon && <Icon name={icon || iconName(type)} type={type} />}

				{!isBanner && (
					<NotificationTextWrapper>
						{Boolean(title) && <NotificationTitle>{label}</NotificationTitle>}
						<NotificationDescription>{message}</NotificationDescription>

						{(Boolean(actionLinkTitle) || Boolean(helperLinkTitle)) && (
							<ActionsWrapper top={6}>
								{Boolean(actionLinkTitle) && Boolean(onActionClick) && (
									<>
										{actionLinkTooltip ? (
											<TooltipComponent title={actionLinkTooltip}>
												<span>{actionLink}</span>
											</TooltipComponent>
										) : (
											actionLink
										)}
									</>
								)}
								{Boolean(helperLinkTitle) && Boolean(onHelperClick) && (
									<Indent left={12}>
										{helperLinkTooltip ? (
											<TooltipComponent title={helperLinkTooltip}>
												<span>{helperLink}</span>
											</TooltipComponent>
										) : (
											helperLink
										)}
									</Indent>
								)}
							</ActionsWrapper>
						)}
					</NotificationTextWrapper>
				)}

				{isBanner && (
					<>
						<BannerText>{message}</BannerText>
						{Boolean(url) && Boolean(linkTitle) && (
							<Indent left={12}>
								<LinkComponent
									size={UISizes.SMALL}
									type={type}
									title={linkTitle}
									url={getValidLink(url)}
									external={external}
								/>
							</Indent>
						)}
					</>
				)}
			</ContentContainer>

			{Boolean(onClose) && (
				<IconClose name="close" onClick={() => onClose()} type={UITypes.PASSIVE} />
			)}
		</NotificationContainer>
	)
}
